import { Status, useInsightPanelDataFetcher } from "./useInsightPanelDataFetcher"
import {
  InsightsPanel,
  InsightsPanelContent,
  InsightsPanelErrorDefault,
  InsightsPanelHeader,
  InsightsPanelNoDataDefault,
} from "~/src/components/Dashboard/InsightsPanel"
import { Image } from "~/src/components/Image"
import { Format } from "~/src/lib/format"
import { WithErrors } from "~/src/types/withErrors"

type PopularProductDataRow = {
  name: string
  inStock: number | string
  sold: number
  imageUrl?: string
  productHref: string
}

export type PopularProductsData = WithErrors<{ products: PopularProductDataRow[] }>

export type StorefrontInsightsPopularProductsPanelRenderProps = {
  status: Status
  products?: PopularProductDataRow[]
}

const NUMBER_OF_PRODUCTS_SHOWN = 5

export function StorefrontInsightsPopularProductsPanel() {
  const { isLoading, data } = useInsightPanelDataFetcher<PopularProductsData>(`popular_products`)

  // TODO: update this when we do pagination, currently limiting to {NUMBER_OF_PRODUCTS_SHOWN}
  const products = data?.products?.slice(0, NUMBER_OF_PRODUCTS_SHOWN)

  let status: Status
  if (isLoading) {
    status = "loading"
  } else if (data?.errors) {
    status = "error"
  } else if (products && products.length > 0) {
    status = "success"
  } else {
    status = "noData"
  }

  return <StorefrontInsightsPopularProductsRenderPanel status={status} products={products} />
}

export function StorefrontInsightsPopularProductsRenderPanel({
  status,
  products,
}: StorefrontInsightsPopularProductsPanelRenderProps) {
  return (
    <InsightsPanel className="@container h-full w-full">
      <InsightsPanelHeader title="Popular products" />
      <InsightsPanelContent className="overflow-y-auto">
        {/* mobile */}
        <div className="h-full @sm:hidden">
          <PopularProductsContent status={status} variant="list" rows={products} />
        </div>
        {/* desktop */}
        <div className="hidden h-full @sm:block">
          <PopularProductsContent status={status} variant="table" rows={products} />
        </div>
      </InsightsPanelContent>
    </InsightsPanel>
  )
}

function PopularProductsContent({
  status,
  variant,
  rows,
}: {
  status: Status
  variant: "list" | "table"
  rows: PopularProductDataRow[] | undefined
}) {
  if (status === "loading") return variant === "list" ? <PopularProductListSkeleton /> : <PopularProductTableSkeleton />
  if (status === "error") return <InsightsPanelErrorDefault />
  if (status === "noData" || !rows) return <InsightsPanelNoDataDefault />
  return variant === "list" ? <PopularProductList rows={rows} /> : <PopularProductTable rows={rows} />
}

function PopularProductTable({ rows }: { rows: PopularProductDataRow[] | undefined }) {
  return (
    <table className="max-h-96 w-full overflow-y-auto [&_td]:py-4 [&_th]:py-2">
      <thead className="sticky top-0 bg-slate-200 text-xs font-semibold text-slate-700">
        <tr className="border-b">
          <th className="pl-4 text-left">PRODUCT</th>
          <th className="pr-4 text-right whitespace-nowrap">IN STOCK</th>
          <th className="pr-4 text-right">SOLD</th>
        </tr>
      </thead>
      <tbody>
        {rows?.map((product) => (
          <tr key={product.name} className="border-b py-4">
            <td className="flex items-center gap-2 pl-4">
              <Image src={product.imageUrl} alt={product.name} className="h-8 w-8 rounded object-cover text-gray-700" />
              <p>{product.name}</p>
            </td>
            <td className="pr-4 text-right">
              {typeof product.inStock === "number" ? Format.number(product.inStock) : product.inStock}
            </td>
            <td className="pr-4 text-right">{Format.number(product.sold)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function PopularProductList({ rows }: { rows: PopularProductDataRow[] | undefined }) {
  return (
    <>
      {rows?.map((product) => (
        <div key={product.name} className="flex gap-6 border-b p-4">
          <Image src={product.imageUrl} alt={product.name} className="h-12 w-12 rounded object-cover" />
          <div className="flex flex-col gap-1">
            <p className="mt-[-6px]">{product.name}</p>
            <p className="text-gray-500">
              In stock: {typeof product.inStock === "number" ? Format.number(product.inStock) : product.inStock}
            </p>
            <p className="text-gray-500">Sold: {Format.number(product.sold)}</p>
          </div>
        </div>
      ))}
    </>
  )
}

function PopularProductTableSkeleton() {
  return (
    <table className="w-full [&_td]:py-4 [&_th]:py-2">
      <thead className="bg-slate-200 text-xs font-semibold text-slate-700">
        <tr className="border-b">
          <th className="pl-4 text-left">Product</th>
          <th className="text-right">In stock</th>
          <th className="pr-4 text-right">Sold</th>
        </tr>
      </thead>
      <tbody>
        {[...Array(NUMBER_OF_PRODUCTS_SHOWN)].map((_, idx) => (
          <tr key={idx} className="animate-pulse border-b">
            <td className="flex items-center gap-2 pl-4">
              <div className="h-8 w-8 rounded-full bg-zinc-200" />
              <div className="h-3 w-48 rounded-full bg-zinc-200" />
            </td>
            <td className="text-right">
              <div className="ml-auto h-3 w-12 rounded-full bg-zinc-200" />
            </td>
            <td className="pr-4 text-right">
              <div className="ml-auto h-3 w-12 rounded-full bg-zinc-200" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function PopularProductListSkeleton() {
  return (
    <>
      {[...Array(NUMBER_OF_PRODUCTS_SHOWN)].map((_, idx) => (
        <div key={idx} className="flex animate-pulse gap-4 border-b p-4">
          <div className="h-8 w-8 rounded-full bg-zinc-200" />
          <div className="flex flex-col gap-2">
            <div className="h-2 w-48 rounded-full bg-zinc-200" />
            <div className="h-2 w-24 rounded-full bg-zinc-200" />
            <div className="h-2 w-12 rounded-full bg-zinc-200" />
          </div>
        </div>
      ))}
    </>
  )
}
